import {
  Await,
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Stack, CircularProgress, Box, Typography } from "@mui/material";
import { GenericTable, TableColumn } from "../components/GenericTable.tsx";
import { z } from "zod";
import { ensureAuthenticated } from "../utils/auth.ts";
import { Route as ProductGroupRoute } from "./cooling-regimes-product-group.tsx";

import { FilterSidebarWrapper } from "../components/filterSidebar/FilterSidebarWrapper.tsx";
import { AutocompleteFilter } from "../components/filterSidebar/AutocompleteFilter.tsx";
import FilterSidebarButton from "../components/filterSidebar/FilterSidebarButton.tsx";
import { FilterSidebarLayout } from "../components/filterSidebar/FilterSidebarLayout.tsx";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { createCoolingRegimeProductGroupDeferredLoader } from "../loaders/coolingRegimeProductGroupLoader.ts";
import { routerHead } from "../utils/head.ts";

import { useTranslation } from "react-i18next";
import type { CoolingRegimeProductGroupLoaderResult } from "../loaders/coolingRegimeProductGroupLoader.ts";
import { useEffect, useState } from "react";
import { DetailDrawer } from "../components/DetailDrawer.tsx";
import { getProductGroupDetailTabs } from "../components/ProductGroupDetailTabs/getProductGroupDetailTabs.tsx";
import { useSetAtom } from "jotai";
import { filterSidebarAtom } from "../atoms/filterSidebarAtom.ts";

export interface ProductGroupRow {
  coolingRegimeProductGroupId: string;
  code: number;
  name: string;
  startDate: string;
  endDate: string;
  vbnChangeDateTime?: string;
}

const searchSchema = z.object({
  page: z.string().optional(),
  size: z.string().optional(),
  registrationCode: z.string().optional(),
  productGroupName: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/cooling-regimes-product-group")({
  beforeLoad: ensureAuthenticated,
  loaderDeps: ({ search }: { search: SearchParams }) => ({
    page: search.page,
    size: search.size,
    registrationCode: search.registrationCode,
    productGroupName: search.productGroupName,
    startDate: search.startDate,
    endDate: search.endDate,
  }),
  loader: async ({ location }: { location: { search: SearchParams } }) => {
    const filters = {
      page: location.search.page ? parseInt(location.search.page) : 1,
      pageSize: location.search.size ? parseInt(location.search.size) : 10,
      registrationCode: location.search.registrationCode,
      productGroupName: location.search.productGroupName,
      startDate: location.search.startDate,
      endDate: location.search.endDate,
    };
    return createCoolingRegimeProductGroupDeferredLoader(filters);
  },
  validateSearch: searchSchema,
  component: RouteComponent,
  head: routerHead("Productgroepen"),
});

const productGroupColumns = (t: (key: string) => string): TableColumn<ProductGroupRow>[] => [
  { key: "code", label: t("productGroup.code") },
  { key: "name", label: t("productGroup.name") },
  { key: "startDate", label: t("productGroup.startDate") },
  { key: "endDate", label: t("productGroup.endDate") },
  { key: "vbnChangeDateTime", label: t("productGroup.vbnChangeDateTime") },
];

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: ProductGroupRoute.id });
  const loaderData = Route.useLoaderData() as {
    data: Promise<CoolingRegimeProductGroupLoaderResult>;
  };
  const [selectedProductGroupId, setSelectedProductGroupId] = useState<string | null>(null);
  const [selectedProductGroupCode, setSelectedProductGroupCode] = useState<string | null>(
    null,
  );
  const setFilterOpen = useSetAtom(filterSidebarAtom);
  
  // Lokale state voor datepickers om visuele feedback tijdens typen te behouden
  const [localStartDate, setLocalStartDate] = useState<Dayjs | null>(
    search.startDate ? dayjs(search.startDate) : null
  );
  const [localEndDate, setLocalEndDate] = useState<Dayjs | null>(
    search.endDate ? dayjs(search.endDate) : null
  );

  // Ensure filters are open on productgroup screen startup
  useEffect(() => {
    setFilterOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedProductGroupId(null);
    setSelectedProductGroupCode(null);
  }, [
    search.registrationCode,
    search.productGroupName,
    search.startDate,
    search.endDate,
  ]);

  // Sync lokale state met search params
  useEffect(() => {
    const newStartValue = search.startDate ? dayjs(search.startDate) : null;
    const newEndValue = search.endDate ? dayjs(search.endDate) : null;
    
    setLocalStartDate(newStartValue);
    setLocalEndDate(newEndValue);
  }, [search.startDate, search.endDate]);

  const registrationCode = search.registrationCode ?? null;
  const productGroupName = search.productGroupName ?? null;
  const startDate = search.startDate ? dayjs(search.startDate) : null;
  const endDate = search.endDate ? dayjs(search.endDate) : null;

  const filterCount =
    (registrationCode ? 1 : 0) +
    (productGroupName ? 1 : 0) +
    (startDate ? 1 : 0) +
    (endDate ? 1 : 0);

  function updateFilters(newFilters: Partial<typeof search>) {
    navigate({
      to: ProductGroupRoute.fullPath,
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: "1",
      }),
    });
  }

  function handleClearFilters() {
    updateFilters({
      registrationCode: undefined,
      productGroupName: undefined,
      startDate: undefined,
      endDate: undefined,
    });
  }

  return (
    <>
      <FilterSidebarWrapper
        activeFilters={filterCount}
        onClearFilters={handleClearFilters}
      >
        <AutocompleteFilter
          filterName="registrationCode"
          label={t("productGroup.code")}
          value={registrationCode ?? null}
          onChange={(value) =>
            updateFilters({
              registrationCode: value ? String(value) : undefined,
            })
          }
          placeholder={t("productGroup.codePlaceholder")}
        />

        <AutocompleteFilter
          filterName="productGroupName"
          label={t("productGroup.name")}
          value={productGroupName ?? null}
          onChange={(value) =>
            updateFilters({ productGroupName: value ? String(value) : undefined })
          }
          placeholder={t("productGroup.namePlaceholder")}
        />

        <Stack spacing={1}>
          <DatePicker
            key={search.startDate || 'start-empty'}
            format="DD/MM/YYYY"
            label={t("productGroup.startDate")}
            value={localStartDate}
            onChange={(newValue: Dayjs | null) => {
              setLocalStartDate(newValue);
              
              // Auto-trigger alleen als het een volledige, geldige datum is met realistisch jaartal
              // Dit voorkomt trigger op incomplete jaren zoals 0002, 0020, 0202
              if (newValue?.isValid() && newValue.year() >= 1900) {
                updateFilters({
                  startDate: newValue.toISOString(),
                });
              } else if (newValue === null) {
                updateFilters({ startDate: undefined });
              }
            }}
            slotProps={{
              textField: { 
                size: "small",
                onBlur: () => {
                  // Trigger bij blur, maar alleen als de datum geldig is
                  if (localStartDate?.isValid() && localStartDate.year() >= 1900) {
                    updateFilters({
                      startDate: localStartDate.toISOString(),
                    });
                  }
                },
                onKeyDown: (event) => {
                  // Trigger op Enter key
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (localStartDate?.isValid() && localStartDate.year() >= 1900) {
                      updateFilters({
                        startDate: localStartDate.toISOString(),
                      });
                    }
                  }
                }
              },
              field: { 
                clearable: true, 
                onClear: () => {
                  setLocalStartDate(null);
                  updateFilters({ startDate: undefined });
                }
              },
            }}
          />
          <DatePicker
            key={search.endDate || 'end-empty'}
            format="DD/MM/YYYY"
            label={t("productGroup.endDate")}
            value={localEndDate}
            onChange={(newValue: Dayjs | null) => {
              setLocalEndDate(newValue);
              
              // Auto-trigger alleen als het een volledige, geldige datum is met realistisch jaartal
              // Dit voorkomt trigger op incomplete jaren zoals 0002, 0020, 0202
              if (newValue?.isValid() && newValue.year() >= 1900) {
                updateFilters({
                  endDate: newValue.toISOString(),
                });
              } else if (newValue === null) {
                updateFilters({ endDate: undefined });
              }
            }}
            slotProps={{
              textField: { 
                size: "small",
                onBlur: () => {
                  // Trigger bij blur, maar alleen als de datum geldig is
                  if (localEndDate?.isValid() && localEndDate.year() >= 1900) {
                    updateFilters({
                      endDate: localEndDate.toISOString(),
                    });
                  }
                },
                onKeyDown: (event) => {
                  // Trigger op Enter key
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    if (localEndDate?.isValid() && localEndDate.year() >= 1900) {
                      updateFilters({
                        endDate: localEndDate.toISOString(),
                      });
                    }
                  }
                }
              },
              field: { 
                clearable: true, 
                onClear: () => {
                  setLocalEndDate(null);
                  updateFilters({ endDate: undefined });
                }
              },
            }}
          />
        </Stack>
      </FilterSidebarWrapper>

      <FilterSidebarLayout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 120px)",
            overflow: "auto",
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              flexShrink: 0,
            }}
          >
            <Typography 
              variant="h1" 
              gutterBottom 
              sx={{ 
                mb: 0,
                fontSize: "1.75rem",
                fontWeight: 600,
                fontFamily: "var(--font-family-halyard-display)",
              }}
            >
              {t("productGroup.overviewTitle")}
            </Typography>
            <FilterSidebarButton />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              position: "relative",
              "& > .MuiCircularProgress-root": {
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              },
            }}
          >
            <Await promise={loaderData.data} fallback={<CircularProgress />}>
          {(data: CoolingRegimeProductGroupLoaderResult) => {
            if (data.error) {
              return <div>Error: {data.error}</div>;
            }
            const hasNoData = !data.rows || data.rows.length === 0;
            let emptyMessage: string | undefined;
            if (hasNoData && data.totalCount === 0 && filterCount > 0) {
              emptyMessage = t("productGroup.emptyStates.noMatchingFilters");
            }
            return (
              <GenericTable
                columns={productGroupColumns(t)}
                rows={data.rows}
                rowCount={data.totalCount}
                defaultPageSize={10}
                pageSizeOptions={[10, 25, 50, 100]}
                emptyMessage={hasNoData ? emptyMessage : undefined}
                selectedRowId={selectedProductGroupId ?? undefined}
                getRowId={(row) => row.coolingRegimeProductGroupId}
                onRowClick={(row) => {
                  setSelectedProductGroupId(row.coolingRegimeProductGroupId);
                  setSelectedProductGroupCode(String(row.code));
                }}
                onPageChange={(page) => {
                  navigate({
                    to: ProductGroupRoute.fullPath,
                    search: { ...search, page: String(page) },
                  });
                }}
                onPageSizeChange={(pageSize) => {
                  navigate({
                    to: ProductGroupRoute.fullPath,
                    search: { ...search, size: String(pageSize), page: "1" },
                  });
                }}
              />
            );
          }}
        </Await>
          </Box>

          <DetailDrawer
            open={!!selectedProductGroupId}
            onClose={() => {
              setSelectedProductGroupId(null);
              setSelectedProductGroupCode(null);
            }}
            tabs={
              selectedProductGroupCode
                ? getProductGroupDetailTabs({ productGroupCode: selectedProductGroupCode })
                : []
            }
          />
        </Box>
      </FilterSidebarLayout>
    </>
  );
}
